import { getItemCategories, getPricing } from "@/api/settings";
import { Backdrop, Box, CircularProgress, Stack } from "@mui/material";
import { useAsync, useMountEffect } from "@react-hookz/web";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PricingScopeProvider } from "../PricingScopeProvider";
import { PricingScope } from "../pricingScope";
import { DeliveryWindowsForm } from "./DeliveryWindowsForm";
import { DiscountCodesForm } from "./DiscountCodesForm";
import { FloorLevelsForm } from "./FloorLevelsForm";
import { ItemCategoriesForm } from "./ItemCategoriesForm";
import { ItemSizesForm } from "./ItemSizesForm";
import { MiscCostsForm } from "./MiscCostsForm";
import { HighValueForm } from "./HighValueItemForm";
import { DistanceSurchargeForm } from "./DistanceSurchargeForm";
import { PricingScopeSelector } from "./PricingScopeSelector";
import { StorePricingEditor } from "./StorePricingEditor";

const GLOBAL_SCOPE: PricingScope = { storeId: null, storeName: null };

export const PricingSettings = () => {
    const [scope, setScope] = useState<PricingScope>(GLOBAL_SCOPE);
    const [getPricingState, getPricingActions] = useAsync(getPricing);

    const {
        isPending: isCategoriesPending,
        isError: isCategoriesError,
        data: categories,
    } = useQuery({
        queryKey: ["item-categories"],
        queryFn: getItemCategories,
    });

    useMountEffect(() => {
        getPricingActions.execute();
    });

    if (getPricingState.status === "loading" || isCategoriesPending)
        return (
            <Backdrop
                open={
                    getPricingState.status === "loading" || isCategoriesPending
                }
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <CircularProgress sx={{ color: "#ffffff" }} />
            </Backdrop>
        );

    if (
        getPricingState.status === "success" &&
        getPricingState.result &&
        !isCategoriesError &&
        categories.items
    ) {
        const isGlobal = scope.storeId === null;

        return (
            <PricingScopeProvider scope={scope}>
                <Box maxWidth={"xl"} marginX={"auto"}>
                    <Stack spacing={4}>
                        <PricingScopeSelector
                            scope={scope}
                            onChange={setScope}
                        />

                        {isGlobal ? (
                            /* Global scope renders exactly what it always has. The
                               store-scoped surface is additive, so this path cannot
                               be regressed by the per-store work. */
                            <Stack
                                direction={{ md: "row", sm: "column" }}
                                gap={4}
                            >
                                <Stack spacing={4} flex={2}>
                                    <ItemSizesForm
                                        itemSizes={
                                            getPricingState.result.item_sizes
                                        }
                                    />
                                    <DeliveryWindowsForm
                                        deliveryWindows={
                                            getPricingState.result
                                                .delivery_windows
                                        }
                                    />
                                    <FloorLevelsForm
                                        floorLevels={
                                            getPricingState.result.floor_levels
                                        }
                                    />
                                </Stack>
                                <Stack flex={1} spacing={4}>
                                    <MiscCostsForm
                                        miscCosts={
                                            getPricingState.result.misc_costs
                                        }
                                    />
                                    {/* Item categories and discount codes are NOT
                                        store-scoped: they carry no store_id and are
                                        configured globally. Under a store scope they
                                        are HIDDEN rather than shown, because an edit
                                        made beneath a header naming a store would
                                        silently write GLOBAL and change every store's
                                        configuration. Same failure class as bead
                                        pollytech-llc-v8q, with a wider blast radius. */}
                                    <ItemCategoriesForm
                                        itemCategories={categories.items}
                                    />
                                    <DiscountCodesForm />
                                    <HighValueForm />
                                    <DistanceSurchargeForm />
                                </Stack>
                            </Stack>
                        ) : (
                            <StorePricingEditor />
                        )}
                    </Stack>
                </Box>
            </PricingScopeProvider>
        );
    }
};
